import React from 'react'
import useForm from 'react-hook-form'
import { RHFInput } from 'react-hook-form-input'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Grid,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { Button } from '@yapay/react-components'

import { InputCurrency } from 'components/Form'

import schemas from '../schemas'
import * as service from 'service'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'

const useStyles = makeStyles(theme => ({
  showTextField: { display: 'block' },
  hideTextField: { display: 'none' },
}))

const SettingsPenaltyForm = ({
  edit,
  penalty,
  setEdit,
  onEvent = () => {},
}) => {
  const snackbar = useSnackbar()
  const classes = useStyles()
  const {
    register,
    setValue,
    handleSubmit,
    watch,
    errors,
    unregister,
  } = useForm({
    validationSchema: schemas.penaltySchema,
    defaultValues: {
      ...penalty,
      interestRate: helpers.formatNumber.isNumber(penalty?.interestRate)
        ? String(penalty?.interestRate)
        : '0',
      finePercentage: helpers.formatNumber.isNumber(penalty?.finePercentage)
        ? String(penalty?.finePercentage)
        : '0',
      gracePeriodDays: helpers.formatNumber.isNumber(penalty?.gracePeriodDays)
        ? String(penalty?.gracePeriodDays)
        : '0',
      typeTax: penalty.finePercentage ? 'percentage' : 'value',
    },
  })

  const taxType = watch('typeTax')
  const [isLoading, setIsloading] = React.useState(false)

  const onSubmit = async formData => {
    try {
      setIsloading(true)

      await service.recurrence.penalties.create({
        penalty: {
          interestRate: formData.interestRate || 0,
          fineValue: formData.typeTax === 'value' ? formData.fineValue : 0,
          finePercentage:
            formData.typeTax === 'percentage' ? formData.finePercentage : 0,
          gracePeriodDays: formData.gracePeriodDays,
        },
        updateOldContracts: formData.updateOldContracts || false,
      })

      snackbar.open({
        message: 'Configurações de juros e multa alteradas com sucesso',
        variant: 'success',
      })

      setEdit(false)
      onEvent(true)
    } catch (error) {
      snackbar.open({
        message: `Erro ao alterar informações`,
        variant: 'error',
      })
    } finally {
      setIsloading(false)
    }
  }

  const onCancel = () => {
    setValue('interestRate', String(penalty?.interestRate), true)
    setValue('finePercentage', String(penalty?.finePercentage), true)
    setValue('fineValue', penalty.fineValue, true)
    setValue('gracePeriodDays', String(penalty?.gracePeriodDays), true)
    setValue('typeTax', penalty.fineValue ? 'value' : 'percentage')
    setEdit(false)
  }

  const handleChangeTypeTax = event => {
    if (event.target.value === 'value') setValue('finePercentage', 0, true)
    else setValue('fineValue', 0, true)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box p={2}>
        <Typography color="textSecondary">
          Percentual de juros ao mês que deverá ser acrescido ao valor da fatura
          em atraso
        </Typography>
        <Box mt={2} maxWidth={145}>
          <RHFInput
            as={
              <TextField
                type="number"
                margin="dense"
                label="Juros ao mês"
                fullWidth
                disabled={!edit}
                inputProps={{ min: 0, step: 'any' }}
                error={edit && !!errors.interestRate}
                helperText={edit && errors?.interestRate?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            }
            register={register}
            setValue={setValue}
            name="interestRate"
            mode="onChange"
          />
        </Box>
      </Box>
      <Box p={2}>
        <Typography color="textSecondary">
          Taxa fixa de multa para faturas pagas após o vencimento
        </Typography>
        {edit && (
          <Box mt={2}>
            <RHFInput
              as={
                <RadioGroup name="typeTax" row>
                  <Box mr={5}>
                    <FormControlLabel
                      value="value"
                      control={<Radio />}
                      label={
                        <Typography color="textSecondary">
                          Taxa em reais
                        </Typography>
                      }
                    />
                  </Box>
                  <FormControlLabel
                    value="percentage"
                    control={<Radio />}
                    label={
                      <Typography color="textSecondary">
                        Taxa em percentual sobre o valor da fatura
                      </Typography>
                    }
                  />
                </RadioGroup>
              }
              name="typeTax"
              register={register}
              unregister={unregister}
              setValue={setValue}
              onChange={handleChangeTypeTax}
              mode="onChange"
            />
          </Box>
        )}
        <Box mt={2} maxWidth={145}>
          <Box
            className={
              taxType === 'value'
                ? classes.showTextField
                : classes.hideTextField
            }
          >
            <RHFInput
              as={
                <InputCurrency
                  label="Taxa de multa"
                  margin="dense"
                  disabled={!edit}
                  fullWidth
                  error={!!errors.fineValue}
                />
              }
              name="fineValue"
              register={register}
              setValue={setValue}
            />
          </Box>
          <Box
            className={
              taxType === 'percentage'
                ? classes.showTextField
                : classes.hideTextField
            }
          >
            <RHFInput
              as={
                <TextField
                  type="number"
                  margin="dense"
                  label="Taxa de multa"
                  fullWidth
                  inputProps={{ min: 0, step: 'any' }}
                  disabled={!edit}
                  error={edit && !!errors.finePercentage}
                  helperText={edit && errors?.finePercentage?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              }
              name="finePercentage"
              register={register}
              setValue={setValue}
              mode="onChange"
            />
          </Box>
        </Box>
      </Box>
      <Box p={2}>
        <Typography color="textSecondary">
          Dias de carência (corridos) para cobrança da multa
        </Typography>
        <Box mt={2} maxWidth={145}>
          <RHFInput
            as={
              <TextField
                type="number"
                margin="dense"
                label="Dias de carência"
                fullWidth
                inputProps={{ min: 0 }}
                disabled={!edit}
                error={edit && !!errors.gracePeriodDays}
                helperText={edit && errors?.gracePeriodDays?.message}
              />
            }
            register={register}
            setValue={setValue}
            name="gracePeriodDays"
            mode="onChange"
          />
        </Box>
      </Box>
      {edit && (
        <Box p={2}>
          <RHFInput
            as={
              <FormControlLabel
                control={<Checkbox />}
                label={
                  <Typography component="div" color="textSecondary">
                    <Box fontWeight="fontWeightBold">
                      Aplicar o percentual de juros e taxa de multa para todas
                      as assinaturas
                    </Box>
                  </Typography>
                }
              />
            }
            register={register}
            setValue={setValue}
            type="checkbox"
            name="updateOldContracts"
          />
          <Box ml={4}>
            <Typography variant="body2" color="textSecondary">
              Ao marcar esta opção, o percentual de juros e taxa de multa serão
              aplicados tanto para as novas assinaturas, como para as
              assinaturas já existentes.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Não marque esta opção caso deseje que os percentuais se apliquem
              apenas para as novas assinaturas.
            </Typography>
          </Box>
        </Box>
      )}
      {edit && (
        <Box p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={2}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                isLoading={isLoading}
                fullWidth
              >
                Salvar
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={onCancel}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </form>
  )
}

export default SettingsPenaltyForm
